import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, inr, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { WhatsappLogo, Bell, Warning, Envelope, Timer, Robot } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ReminderAutomation from "@/pages/ReminderAutomation";

function reminderText({ invoice, company, tone = "friendly" }) {
  const path = invoice.share_path || "invoice";
  const shareUrl = `${window.location.origin}/p/${path}/${invoice.id}?t=${invoice.share_token}`;
  if (tone === "urgent") {
    return `Dear ${invoice.customer_name.split(" ")[0] || "Customer"},

This is a follow-up reminder regarding an *overdue* payment.

*Invoice ${invoice.number}*
Amount: ${inr(invoice.grand_total)}
Balance Due: *${inr(invoice.balance_due)}*
Due Date: ${invoice.due_date}
Overdue by: *${invoice.days_overdue} days*

Kindly clear the outstanding balance at the earliest to avoid disruption in future orders.

View / Pay: ${shareUrl}

For any queries, please reach out.

Regards,
${company?.name || "Vetrich"}`;
  }
  return `Dear ${invoice.customer_name.split(" ")[0] || "Customer"},

A gentle reminder that the following invoice is due for payment.

*Invoice ${invoice.number}*
Date: ${invoice.date}
Amount: ${inr(invoice.grand_total)}
Balance Due: *${inr(invoice.balance_due)}*
${invoice.due_date ? `Due Date: ${invoice.due_date}` : ""}

View / Pay: ${shareUrl}

Thank you for your continued business.

Regards,
${company?.name || "Vetrich"}`;
}

function buildWaLink(invoice, company) {
  const tone = invoice.days_overdue > 7 ? "urgent" : "friendly";
  const text = reminderText({ invoice, company, tone });
  const digits = String(invoice.customer_phone || "").replace(/[^0-9]/g, "");
  return digits
    ? `https://wa.me/${digits}?text=${encodeURIComponent(text)}`
    : `https://wa.me/?text=${encodeURIComponent(text)}`;
}

export default function Reminders() {
  const { activeCompanyId, companies, activeStoreId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overdue");

  const activeCompany = companies.find((c) => c.id === activeCompanyId);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/reminders"); setRows(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (activeCompanyId) load(); }, [activeCompanyId, activeStoreId]);

  const buckets = useMemo(() => {
    const overdue = rows.filter((r) => r.days_overdue > 0);
    const dueToday = rows.filter((r) => r.days_overdue === 0);
    const upcoming = rows.filter((r) => r.days_overdue < 0);
    return { overdue, dueToday, upcoming, all: rows };
  }, [rows]);

  const totalDue = buckets.overdue.reduce((s, r) => s + r.balance_due, 0);

  const sendOne = async (r) => {
    window.open(buildWaLink(r, activeCompany), "_blank", "noopener,noreferrer");
    try { await api.post(`/sales-invoices/${r.id}/reminder-sent`); load(); }
    catch (err) { console.warn("reminder-sent log failed", err); }
    toast.success(`WhatsApp opened for ${r.customer_name}`);
  };

  const sendBulk = async () => {
    const targets = tab === "overdue" ? buckets.overdue : buckets[tab] || buckets.all;
    if (targets.length === 0) { toast.error("Nothing to send"); return; }
    if (!confirm(`Open ${targets.length} WhatsApp conversation(s)? Your browser may block popups; please allow them.`)) return;
    for (const r of targets) {
      window.open(buildWaLink(r, activeCompany), "_blank", "noopener,noreferrer");
      try { await api.post(`/sales-invoices/${r.id}/reminder-sent`); }
      catch (err) { console.warn("reminder-sent log failed", err); }
      await new Promise((res) => setTimeout(res, 400));
    }
    toast.success(`Sent ${targets.length} reminder(s)`);
    load();
  };

  const list = buckets[tab] || [];
  const [mode, setMode] = React.useState("manual");

  return (
    <div className="page-shell space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Collections</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1 flex items-center gap-2">
            <Bell size={26} weight="fill" className="text-warning" style={{ color: "hsl(var(--warning))" }} />
            Payment Reminders
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Send WhatsApp reminders manually, or configure the automation engine to fire on selected trigger days after due date.
          </p>
        </div>
        {mode === "manual" && (
          <Button
            onClick={sendBulk}
            disabled={loading || list.length === 0}
            className="h-9 bg-[#25D366] hover:bg-[#128C7E] text-white"
            data-testid="bulk-reminder-btn"
          >
            <WhatsappLogo weight="fill" size={16} className="mr-1.5" />
            Send All ({list.length})
          </Button>
        )}
      </div>

      <Tabs value={mode} onValueChange={setMode}>
        <TabsList data-testid="mode-tabs">
          <TabsTrigger value="manual" data-testid="mode-manual"><Bell size={13} className="mr-1.5" /> Manual Send</TabsTrigger>
          <TabsTrigger value="automation" data-testid="mode-automation"><Robot size={13} className="mr-1.5" /> Automation (Scheduled)</TabsTrigger>
        </TabsList>

        <TabsContent value="automation" className="mt-5">
          <ReminderAutomation />
        </TabsContent>

        <TabsContent value="manual" className="mt-5 space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="kpi">
          <div className="eyebrow flex items-center gap-1.5"><Warning size={11} weight="fill" style={{ color: "hsl(var(--destructive))" }} /> Overdue</div>
          <div className="val text-destructive">{buckets.overdue.length}</div>
          <div className="text-[12px] font-mono mt-1 text-muted-foreground">{inr(totalDue)}</div>
        </div>
        <div className="kpi">
          <div className="eyebrow">Due Today</div>
          <div className="val">{buckets.dueToday.length}</div>
        </div>
        <div className="kpi">
          <div className="eyebrow">Upcoming</div>
          <div className="val">{buckets.upcoming.length}</div>
        </div>
        <div className="kpi">
          <div className="eyebrow">Total Pending</div>
          <div className="val">{buckets.all.length}</div>
          <div className="text-[12px] font-mono mt-1 text-muted-foreground">
            {inr(buckets.all.reduce((s, r) => s + r.balance_due, 0))}
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="reminder-tabs">
          <TabsTrigger value="overdue">Overdue ({buckets.overdue.length})</TabsTrigger>
          <TabsTrigger value="dueToday">Due Today ({buckets.dueToday.length})</TabsTrigger>
          <TabsTrigger value="upcoming">Upcoming ({buckets.upcoming.length})</TabsTrigger>
          <TabsTrigger value="all">All Pending ({buckets.all.length})</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4">
          <div className="card-flat overflow-hidden">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Invoice #</th>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Due Date</th>
                  <th className="num">Days</th>
                  <th className="num">Balance</th>
                  <th className="num">Last Reminder</th>
                  <th className="num" style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} className="text-center py-6 text-muted-foreground">Loading…</td></tr>
                ) : list.length === 0 ? (
                  <tr><td colSpan={9} className="text-center py-10 text-muted-foreground">
                    Nothing to remind. All caught up.
                  </td></tr>
                ) : list.map((r) => {
                  const overdue = r.days_overdue > 0;
                  const urgent = r.days_overdue > 7;
                  const isTemp = r.source_type === "temp_invoice";
                  const detailLink = isTemp ? `/payment-pending` : `/sales/${r.id}`;
                  return (
                    <tr key={`${r.source_type || 'sales'}-${r.id}`} data-testid={`reminder-row-${r.id}`}>
                      <td>
                        {isTemp
                          ? <span className="pill pill-warning">Temp Inv</span>
                          : <span className="pill pill-muted">Invoice</span>}
                      </td>
                      <td className="font-mono text-[12.5px]">
                        <Link to={detailLink} className="text-primary hover:underline">{r.number}</Link>
                      </td>
                      <td className="font-medium">{r.customer_name}</td>
                      <td className="font-mono text-[12px]">{r.customer_phone || <span className="text-muted-foreground italic">no phone</span>}</td>
                      <td className="font-mono text-[12px]">{r.due_date || "—"}</td>
                      <td className="num">
                        {overdue ? (
                          <span className={`pill ${urgent ? "pill-danger" : "pill-warning"}`}>
                            {r.days_overdue}d
                          </span>
                        ) : r.days_overdue === 0 ? (
                          <span className="pill pill-info">Today</span>
                        ) : (
                          <span className="text-muted-foreground">{-r.days_overdue}d</span>
                        )}
                      </td>
                      <td className="num font-semibold">{inr(r.balance_due)}</td>
                      <td className="num text-[11.5px] text-muted-foreground">
                        {r.last_reminder_at
                          ? <span className="inline-flex items-center gap-1"><Timer size={11} /> {fmtDate(r.last_reminder_at)} · {r.reminder_count}×</span>
                          : "—"}
                      </td>
                      <td className="num" style={{ textAlign: "right" }}>
                        <Button
                          size="sm"
                          onClick={() => sendOne(r)}
                          className="h-8 bg-[#25D366] hover:bg-[#128C7E] text-white"
                          data-testid={`send-reminder-${r.id}`}
                        >
                          <WhatsappLogo size={13} weight="fill" className="mr-1" />
                          Send
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
        </TabsContent>
      </Tabs>
    </div>
  );
}
